import { Component, ElementRef, ViewChild, ViewContainerRef, AfterViewInit, OnDestroy, input, output, signal, inject } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Router } from '@angular/router';
import { HighlightPipe } from '../../../core/pipes/highlight.pipe';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SearchIcon } from '../../../../assets/icons/search/search';
import { TranslateModule } from '@ngx-translate/core';
import { NgClass } from '@angular/common';
import { ShebaGtmSearchDirective } from 'app/core/services/google-tag-manager/gtm.directive';
import { ElasticsearchService } from 'app/core/services/elasticsearch.service';
import { TemplateRef } from '@angular/core';
import { ALLOWED_CHARS_PATTERN } from 'app/core/constants/regex.constants';
import { LanguageService } from 'app/shared/services/language.service';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'sheba-search-area',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HighlightPipe,
    SearchIcon,
    TranslateModule,
    NgClass,
    ShebaGtmSearchDirective
  ],
  templateUrl: './search-area.component.html',
  styleUrl: './search-area.component.scss'
})
export class SearchAreaComponent implements AfterViewInit, OnDestroy {
  @ViewChild('suggestionsTpl') suggestionsTpl!: TemplateRef<any>;
  @ViewChild('searchContainer') searchContainer!: ElementRef;

  placeholder = input<string>('חיפוש...');
  searchInput = input<string>();
  customClass = input<string>();
  indexName = input<string>();
  emitOnTextChange = input<boolean>(false);
  navigateOnSelect = input<boolean>(false);
  fields = input<string[]>();
  filters = input<{ [key: string]: any }>();

  onSearch = output<string>();
  onSearchTextChanged = output<string>();
  onSuggestionLinkSelected = output<void>();

  searchText = signal('');
  currentInput = signal('');
  suggestions = signal<any[]>([]);
  showSuggestions = false;
  private overlayRef!: OverlayRef;

  readonly allowedCharsPattern = ALLOWED_CHARS_PATTERN;

  readonly overlay = inject(Overlay);
  readonly viewContainerRef = inject(ViewContainerRef);
  readonly elasticsearchService = inject(ElasticsearchService);
  private readonly languageService = inject(LanguageService);
  private readonly router = inject(Router);

  ngAfterViewInit() {
    this.setupOverlay();
    // Initialize currentInput with searchInput if it exists
    if (this.searchInput()) {
      this.currentInput.set(this.searchInput() || '');
    }
  }

  private setupOverlay() {
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.searchContainer)
      .withPositions([{
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
        offsetY: 0
      }])
      .withDefaultOffsetY(8)
      .withFlexibleDimensions(false)
      .withPush(false);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: false,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      width: this.searchContainer.nativeElement.offsetWidth,
      minWidth: this.searchContainer.nativeElement.offsetWidth,
      maxWidth: this.searchContainer.nativeElement.offsetWidth
    });
  }

  onSearchTextChange(searchText: string) {
    this.searchText.set(searchText);
    this.onSearchTextChanged.emit(searchText);
    this.handleAutocompleteSuggestions(searchText);
  }

  search() {
    const searchValue = this.currentInput();
    this.searchText.set(searchValue);
    this.onSearchTextChanged.emit(searchValue);
    this.onSearch.emit(searchValue);
    this.detachOverlay();
  }

  selectSuggestion(suggestion: any) {
    const selectedText = suggestion.title.replace(/<[^>]*>/g, '')
    .replace(/\u00A0/g, ' ')
    .trim();
    if (suggestion.link) {
      const cleanPath = suggestion.link.startsWith('/') ? suggestion.link.substring(1) : suggestion.link;
      this.router.navigate([cleanPath]);
      this.detachOverlay();
      this.onSuggestionLinkSelected.emit();
    } else {
    this.currentInput.set(selectedText);
    this.searchText.set(selectedText);
    this.onSearchTextChanged.emit(selectedText);
    this.detachOverlay();
    this.onSearch.emit(selectedText);
    }
  }

  private attachOverlay() {
    if (this.overlayRef && !this.overlayRef.hasAttached()) {
      const portal = new TemplatePortal(
        this.suggestionsTpl,
        this.viewContainerRef
      );
      this.overlayRef.attach(portal);
    }
  }

  private detachOverlay() {
    if (this.overlayRef) {
      this.overlayRef.detach();
    }
  }

  onBlur() {
    this.detachOverlay();
  }

  ngOnDestroy() {
    if (this.overlayRef) {
      this.overlayRef.dispose();
    }
  }

  private handleAutocompleteSuggestions(searchText: string) {
    const indexName = this.indexName();
    const searchFields = this.fields();
    if (searchText.length >= 3 && indexName) {
      if (indexName.toLowerCase() === environment.elasticsearch.indexes.generalContent) {
        this.elasticsearchService.getMultiIndexAutocompleteSuggestions(searchText, searchFields, this.filters())
          .subscribe(suggestions => {
            this.suggestions.set(suggestions.values || []);
            if (suggestions.values?.length) {
              this.attachOverlay();
            } else {
              this.detachOverlay();
            }
          });
      } else {
        this.elasticsearchService.getAutocompleteSuggestions(
          searchText, 
          indexName.toLowerCase(), 
          searchFields,
          this.filters()
        )
          .subscribe(suggestions => {
            this.suggestions.set(suggestions.values || []);
            if (suggestions.values?.length) {
              this.attachOverlay();
            } else {
              this.detachOverlay();
            }
          });
      }
    } else {
      this.suggestions.set([]);
      this.detachOverlay();
    }
  }

  onInputChange(value: string) {
    this.currentInput.set(value);
    if (this.emitOnTextChange()) {
      this.onSearchTextChanged.emit(value);
    }
    this.handleAutocompleteSuggestions(value);
  }
}
